import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Shield,
    Building2,
    CheckCircle,
    Clock,
    XCircle,
    Search,
    MapPin,
    AlertCircle,
    ExternalLink,
    Lock,
    LogIn,
    FileText,
    Plus
} from 'lucide-react';

const ChapterDirectorDashboard = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading, login } = useAuth();
    const [submissions, setSubmissions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const isChapterDirector = user?.role === 'chapter_director';
    const isAdmin = user?.is_admin;
    const hasAccess = isChapterDirector || isAdmin;

    const fetchData = useCallback(async () => {
        try {
            const [subsRes, statsRes] = await Promise.all([
                api.get('/chapter-director/resources'),
                api.get('/chapter-director/stats')
            ]);
            setSubmissions(subsRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching chapter data:', error);
            toast.error('Failed to load submissions data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!authLoading && isAuthenticated && hasAccess) {
            fetchData();
        } else if (!authLoading) {
            setLoading(false);
        }
    }, [authLoading, isAuthenticated, hasAccess, fetchData]);

    const handleStatusUpdate = async (submissionId, newStatus) => {
        try {
            await api.put(`/chapter-director/resources/${submissionId}/status`, { status: newStatus });
            toast.success(`Submission ${newStatus}`);
            setSubmissions(submissions.map(s =>
                s.submission_id === submissionId ? { ...s, status: newStatus } : s
            ));
            const statsRes = await api.get('/chapter-director/stats');
            setStats(statsRes.data);
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to update status';
            toast.error(msg);
        }
    };

    const filteredSubmissions = submissions.filter(s => {
        const matchesSearch = !searchQuery ||
            (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (s.city || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (s.contact_name || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Not authenticated
    if (!authLoading && !isAuthenticated) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <Card className="max-w-md w-full text-center">
                    <CardContent className="py-12">
                        <Lock className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">Chapter Director Access</h2>
                        <p className="text-muted-foreground mb-6">Please log in to access your dashboard.</p>
                        <Button onClick={login} className="gap-2" data-testid="cd-login-btn">
                            <LogIn className="h-4 w-4" /> Member Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // No access
    if (!authLoading && isAuthenticated && !hasAccess) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <Card className="max-w-md w-full text-center">
                    <CardContent className="py-12">
                        <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
                        <p className="text-muted-foreground mb-6">
                            This dashboard is only available to Chapter Directors and Admins.
                        </p>
                        <Link to="/directory">
                            <Button variant="outline">Go to Public Directory</Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="chapter-director-dashboard">
            {/* Header */}
            <div className="bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                                    <Shield className="h-8 w-8 text-amber-500" />
                                    Chapter Director Dashboard
                                </h1>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="cd-region-badge">
                                    <MapPin className="h-3 w-3 mr-1" />
                                    {stats?.region || user?.chapter_region || 'All Regions'}
                                </Badge>
                                <p className="text-muted-foreground text-sm">
                                    Manage your submitted members and nominations
                                </p>
                            </div>
                        </div>
                        <Link to="/nominate">
                            <Button className="gap-2" data-testid="cd-nominate-btn">
                                <Plus className="h-4 w-4" />
                                Nominate a Member
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <div className="space-y-6">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
                        </div>
                        <Skeleton className="h-96" />
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Stats Cards */}
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cd-stats-grid">
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Total Submissions</p>
                                            <p className="text-3xl font-bold">{stats?.total_submissions || 0}</p>
                                        </div>
                                        <FileText className="h-8 w-8 text-primary/40" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Approved</p>
                                            <p className="text-3xl font-bold text-emerald-600">{stats?.approved || 0}</p>
                                        </div>
                                        <CheckCircle className="h-8 w-8 text-emerald-200" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Pending Review</p>
                                            <p className="text-3xl font-bold text-amber-600">{stats?.pending || 0}</p>
                                        </div>
                                        <Clock className="h-8 w-8 text-amber-200" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Needs Info</p>
                                            <p className="text-3xl font-bold text-orange-600">{stats?.needs_info || 0}</p>
                                        </div>
                                        <AlertCircle className="h-8 w-8 text-orange-200" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Submissions Table */}
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <FileText className="h-5 w-5 text-primary" />
                                            My Submissions
                                        </CardTitle>
                                        <CardDescription>
                                            {filteredSubmissions.length} of {submissions.length} submissions shown
                                        </CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="relative w-full sm:w-52">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                placeholder="Search..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="pl-10"
                                                data-testid="cd-search"
                                            />
                                        </div>
                                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                                            <SelectTrigger className="w-[150px]" data-testid="cd-status-filter">
                                                <SelectValue placeholder="Status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Status</SelectItem>
                                                <SelectItem value="approved">Approved</SelectItem>
                                                <SelectItem value="pending">Pending</SelectItem>
                                                <SelectItem value="needs_info">Needs Info</SelectItem>
                                                <SelectItem value="rejected">Rejected</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {filteredSubmissions.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p className="font-medium mb-1">No submissions yet</p>
                                        <p className="text-sm mb-4">Nominate organizations to grow your chapter's network</p>
                                        <Link to="/nominate">
                                            <Button variant="outline" size="sm" className="gap-2">
                                                <Plus className="h-4 w-4" />
                                                Nominate a Member
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[500px]">
                                        <div className="space-y-2">
                                            {filteredSubmissions.map((s) => (
                                                <div
                                                    key={s.submission_id}
                                                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50"
                                                    data-testid={`cd-submission-${s.submission_id}`}
                                                >
                                                    <div className="flex-1 min-w-0 mr-4">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-medium truncate">{s.name}</span>
                                                            <StatusBadge status={s.status} />
                                                            {s.submission_type === 'trusted_partner_nomination' && (
                                                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                                                    Nomination
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                                            {(s.city || s.state) && (
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin className="h-3 w-3" />
                                                                    {[s.city, s.state].filter(Boolean).join(', ')}
                                                                </span>
                                                            )}
                                                            {s.contact_name && (
                                                                <span>Contact: {s.contact_name}</span>
                                                            )}
                                                            {s.website && (
                                                                <a
                                                                    href={s.website}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="flex items-center gap-1 hover:text-primary"
                                                                >
                                                                    <ExternalLink className="h-3 w-3" />
                                                                    Website
                                                                </a>
                                                            )}
                                                        </div>
                                                        {s.info_request_message && s.status === 'needs_info' && (
                                                            <div className="mt-2 text-sm bg-amber-50 text-amber-800 rounded px-2 py-1 inline-flex items-center gap-1">
                                                                <AlertCircle className="h-3 w-3" />
                                                                Info requested: {s.info_request_message.slice(0, 80)}{s.info_request_message.length > 80 ? '...' : ''}
                                                            </div>
                                                        )}
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            Submitted {new Date(s.created_at).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};

const StatusBadge = ({ status }) => {
    const styles = {
        approved: 'bg-emerald-100 text-emerald-800',
        pending: 'bg-amber-100 text-amber-800',
        needs_info: 'bg-orange-100 text-orange-800',
        rejected: 'bg-red-100 text-red-800',
    };
    const labels = {
        approved: 'Approved',
        pending: 'Pending',
        needs_info: 'Needs Info',
        rejected: 'Rejected',
    };
    return (
        <Badge variant="outline" className={`text-xs ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
            {labels[status] || status || 'unknown'}
        </Badge>
    );
};

export default ChapterDirectorDashboard;
